import { getSpecSnapshotApi } from '@flipster/apis/v2/snapshot';
import { tradeLogger } from '@flipster/utils/datadog';
import { isServer } from '@flipster/utils/env-helper';

import { sanitizeSymbolName, symbolNameToInfo } from './helper';

export interface SymbolInfoProps {
  symbol: string;
  productType: string;
  pricePrecision: number;
  quoteCurrency?: string;
}

export type SymbolInfoBase = ReturnType<typeof symbolInfoBase>;
export const symbolInfoBase = ({ symbol, productType, pricePrecision, quoteCurrency }: SymbolInfoProps) => ({
  symbol,
  description: '',
  exchange: 'Flipster',
  type: productType,
  ticker: symbol,
  // NOTE: tv 차트는 0.1을 10으로, 0.01을 100으로 씀. 우리 precision은 0.1을 1로, 0.01을 2로 씀.
  pricescale: 10 ** pricePrecision,
  quoteCurrency,
});

export const getSymbols = async () => {
  try {
    const { data } = await getSpecSnapshotApi({ showAll: true });
    return Object.values(data.t.instruments.s).map(({ symbol, productType, pricePrecision, quoteCurrency }) =>
      symbolInfoBase({ symbol, productType, pricePrecision, quoteCurrency }),
    );
  } catch {
    return [];
  }
};

export const getHashChartInfo = () => {
  if (isServer) return {};
  const [, hashString] = window.location.hash.split('#');
  const urlParams = new URLSearchParams(hashString);
  const symbol = urlParams.get('symbol');

  return {
    symbol: symbol && sanitizeSymbolName(symbol),
    productType: urlParams.get('product_type'),
    pricePrecision: urlParams.get('price_precision'),
    network: urlParams.get('network'),
    chartPatternId: urlParams.get('chart_pattern_id'),
  };
};

const urlSymbolInfoMemo: Record<string, SymbolInfoBase> = {};
export const getSymbolInfoFromHash = (tvSymbol: string) => {
  const infos = getHashChartInfo();
  const symbol = sanitizeSymbolName(tvSymbol);
  if (symbol !== infos.symbol) {
    tradeLogger.error('chart_not_loaded', {
      error_message: 'Cannot resolve symbol - symbol mismatch',
      error_category: 'http_errors',
      symbol,
      hashSymbol: infos.symbol,
      url: window.location.href,
    });
    return urlSymbolInfoMemo[symbol];
  }
  if (!infos.productType || !infos.pricePrecision) {
    tradeLogger.error('chart_not_loaded', {
      error_message: 'Cannot resolve symbol - invalid url hash',
      error_category: 'http_errors',
      symbol,
      url: window.location.href,
    });
    return;
  }
  return (urlSymbolInfoMemo[symbol] = symbolInfoBase({
    symbol,
    productType: infos.productType,
    pricePrecision: Number(infos.pricePrecision),
  }));
};

export const getResolveSymbolInfoBaseFn = (from: 'hash' | 'api') => {
  return async (tvSymbol: string) => {
    const { symbol, priceType } = symbolNameToInfo(tvSymbol);
    const symbolInfoBase = await (async () => {
      if (from === 'hash') {
        return getSymbolInfoFromHash(tvSymbol);
      }
      const symbols = await getSymbols();
      return symbols.find((s) => s.symbol === symbol);
    })();
    if (!symbolInfoBase) return;
    // BBO mid can land on half-tick — show one extra decimal on the price axis.
    // MARK stays on instrument tick size.
    // ROLLBACK(last-price): perp가 MID로 회귀하며 perp 축도 tick+1 적용 (원본: spot MID만 해당, perp LAST/MARK는 tick).
    // context: https://prextech.slack.com/archives/C07DN09JTUY/p1758073579533969
    if (priceType === 'MID') return { ...symbolInfoBase, pricescale: symbolInfoBase.pricescale * 10 };
    return symbolInfoBase;
  };
};
